import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Tutorials.css";
import useSEO from "../../hooks/useSEO";

import imgExcel from "../../assets/images/TutorialsCard/basic_Excel.WebP";
import imgSQL from "../../assets/images/TutorialsCard/basic_SQL.WebP";
import imgPython from "../../assets/images/TutorialsCard/basic_Python.WebP";
import imgPowerBI from "../../assets/images/TutorialsCard/basic_Power_BI.WebP";
import imgStatistics from "../../assets/images/TutorialsCard/basic_Statistics.WebP";
import imgNumpy from "../../assets/images/TutorialsCard/basic_Numpy.WebP";
import imgPandas from "../../assets/images/TutorialsCard/basic_Pandas.WebP";
import imgMatplotlib from "../../assets/images/TutorialsCard/basic_Matplotlib.WebP";
import imgSeaborn from "../../assets/images/TutorialsCard/basic_Seaborn.WebP";

const TUTORIALS = [
  {
    id: 1,
    category: "Excel",
    categoryClass: "cat-excel",
    image: imgExcel,
    name: "Basic Excel Fundamentals",
    badge: "Popular",
    route: "/tutorials/basic-excel",
    available: true,
    level: "Beginner",
    desc: "Build strong spreadsheet skills with formulas, formatting, tables, and practical workflows used in modern data work.",
    topics: ["Spreadsheet Basics", "Formulas", "Pivot Tables", "Formatting"],
  },
  {
    id: 2,
    category: "SQL",
    categoryClass: "cat-sql",
    image: imgSQL,
    name: "Basic SQL",
    badge: "Popular",
    route: "/tutorials/basic-sql",
    available: true,
    level: "Beginner",
    desc: "Learn how to query databases confidently using SELECT, WHERE, ORDER BY, GROUP BY, and JOIN in a structured way.",
    topics: ["SELECT", "WHERE", "JOIN", "GROUP BY"],
  },
  {
    id: 3,
    category: "Python",
    categoryClass: "cat-python",
    image: imgPython,
    name: "Python Programming Basics",
    badge: "Hot",
    route: "/tutorials/python-basics",
    available: true,
    level: "Beginner",
    desc: "Start Python from zero with variables, loops, conditions, functions, and practical coding foundations for real learning.",
    topics: ["Variables", "Loops", "Functions", "Lists"],
  },
  {
    id: 4,
    category: "Power BI",
    categoryClass: "cat-powerbi",
    image: imgPowerBI,
    name: "Basic Power BI Fundamentals",
    badge: "Soon",
    route: "/tutorials/powerbi-fundamentals",
    available: false,
    level: "Beginner",
    desc: "Create dashboards, connect data sources, and understand the reporting workflow used in modern business intelligence.",
    topics: ["Dashboards", "Reports", "DAX Basics", "Data Connection"],
  },
  {
    id: 5,
    category: "Statistics",
    categoryClass: "cat-statistics",
    image: imgStatistics,
    name: "Basic Statistics",
    badge: "Soon",
    route: "/tutorials/statistics",
    available: false,
    level: "Beginner",
    desc: "Understand probability, averages, distributions, and practical statistical thinking with simple explanations.",
    topics: ["Probability", "Mean & Median", "Distributions", "Hypothesis"],
  },
  {
    id: 6,
    category: "NumPy",
    categoryClass: "cat-numpy",
    image: imgNumpy,
    name: "NumPy Essentials",
    badge: "Soon",
    route: "/tutorials/numpy",
    available: false,
    level: "Intermediate",
    desc: "Learn fast array operations, indexing, vectorisation, and matrix fundamentals that power data science workflows.",
    topics: ["Arrays", "Indexing", "Broadcasting", "Matrix Ops"],
  },
  {
    id: 7,
    category: "Pandas",
    categoryClass: "cat-pandas",
    image: imgPandas,
    name: "Pandas for Data Analysis",
    badge: "Soon",
    route: "/tutorials/pandas",
    available: false,
    level: "Intermediate",
    desc: "Work with real datasets using filtering, cleaning, grouping, merging, and analysis techniques with Pandas.",
    topics: ["DataFrames", "Cleaning", "Merge", "GroupBy"],
  },
  {
    id: 8,
    category: "Matplotlib",
    categoryClass: "cat-matplotlib",
    image: imgMatplotlib,
    name: "Matplotlib Visualizations",
    badge: "Soon",
    route: "/tutorials/matplotlib",
    available: false,
    level: "Intermediate",
    desc: "Create polished charts that help you present insights visually for reporting, analysis, and storytelling.",
    topics: ["Line Chart", "Bar Chart", "Styling", "Annotations"],
  },
  {
    id: 9,
    category: "Seaborn",
    categoryClass: "cat-seaborn",
    image: imgSeaborn,
    name: "Seaborn Data Visualization",
    badge: "Soon",
    route: "/tutorials/seaborn",
    available: false,
    level: "Intermediate",
    desc: "Build elegant statistical visualisations such as heatmaps, pair plots, and advanced charts for better understanding.",
    topics: ["Heatmaps", "Pair Plots", "Stat Plots", "Themes"],
  },
];

const FILTERS = ["All", "Live", "Coming Soon", "Beginner", "Intermediate"];

function TutorialCard({ tutorial, index }) {
  const navigate = useNavigate();

  const handleOpen = () => {
    if (tutorial.available) {
      navigate(tutorial.route);
    }
  };

  const handleButtonClick = (event) => {
    event.stopPropagation();
    if (tutorial.available) {
      navigate(tutorial.route);
    }
  };

  return (
    <article
      className={`tutorial-card ${tutorial.available ? "is-live" : "is-soon"} reveal`}
      style={{ transitionDelay: `${index * 0.06}s` }}
      onClick={handleOpen}
      role={tutorial.available ? "button" : "article"}
      tabIndex={tutorial.available ? 0 : -1}
      aria-label={tutorial.available ? `Open ${tutorial.name}` : `${tutorial.name} coming soon`}
      onKeyDown={(event) => {
        if (tutorial.available && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          navigate(tutorial.route);
        }
      }}
    >
      <div className="tutorial-card-media">
        <img
          src={tutorial.image}
          alt={tutorial.name}
          className="tutorial-card-image"
          loading="lazy"
        />

        <div className="tutorial-card-media-overlay" />

        <div className="tutorial-card-topbar">
          <span className={`tutorial-category ${tutorial.categoryClass}`}>
            {tutorial.category}
          </span>

          <span className={`tutorial-badge ${tutorial.available ? "badge-live" : "badge-soon"}`}>
            {tutorial.badge}
          </span>
        </div>

        {!tutorial.available && (
          <div className="tutorial-soon-overlay">
            <span>Coming Soon</span>
          </div>
        )}
      </div>

      <div className="tutorial-card-body">
        <div className="tutorial-card-content">
          <p className="tutorial-level">{tutorial.level}</p>
          <h3 className="tutorial-title">{tutorial.name}</h3>
          <p className="tutorial-desc">{tutorial.desc}</p>

          <div className="tutorial-topics">
            {tutorial.topics.map((topic) => (
              <span key={topic} className="tutorial-topic-pill">
                {topic}
              </span>
            ))}
          </div>
        </div>

        <div className="tutorial-card-footer">
          {tutorial.available ? (
            <button
              type="button"
              className="tutorial-btn"
              onClick={handleButtonClick}
            >
              Start Tutorial
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8h10M8.5 3.5L13 8l-4.5 4.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="tutorial-btn tutorial-btn-disabled"
              disabled
            >
              Coming Soon
            </button>
          )}

          <span className={`tutorial-status ${tutorial.available ? "status-live" : "status-soon"}`}>
            <span className="status-dot" />
            {tutorial.available ? "Live Now" : "In Progress"}
          </span>
        </div>
      </div>
    </article>
  );
}

function Tutorials() {
  const [activeFilter, setActiveFilter] = useState("All");

  useSEO({
    title: "Free Tutorials — Excel, SQL, Python, Power BI & Data Skills | NextSkillAI",
    description:
      "Learn Excel, SQL, Python, Power BI, Statistics, Pandas, NumPy, Matplotlib, and more with structured free tutorials on NextSkillAI.",
    keywords:
      "NextSkillAI tutorials, Excel tutorial, SQL tutorial, Python basics, Power BI tutorial, statistics tutorial, pandas tutorial, numpy tutorial, data analytics tutorials",
    canonical: "/tutorials",
    schema: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "NextSkillAI Tutorials",
      description: "Structured tutorials in Excel, SQL, Python, Power BI, analytics, and data skills.",
      url: "https://nextskillai.com/tutorials",
      numberOfItems: TUTORIALS.length,
      itemListElement: TUTORIALS.map((tutorial, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: tutorial.name,
        url: `https://nextskillai.com${tutorial.route}`,
        description: tutorial.desc,
      })),
    },
  });

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      elements.forEach((element) => observer.unobserve(element));
      observer.disconnect();
    };
  }, [activeFilter]);

  const totalTutorials = TUTORIALS.length;
  const totalLive = TUTORIALS.filter((item) => item.available).length;
  const totalSoon = TUTORIALS.filter((item) => !item.available).length;

  const filteredTutorials = useMemo(() => {
    switch (activeFilter) {
      case "Live":
        return TUTORIALS.filter((item) => item.available);
      case "Coming Soon":
        return TUTORIALS.filter((item) => !item.available);
      case "Beginner":
        return TUTORIALS.filter((item) => item.level === "Beginner");
      case "Intermediate":
        return TUTORIALS.filter((item) => item.level === "Intermediate");
      default:
        return TUTORIALS;
    }
  }, [activeFilter]);

  return (
    <div className="tutorials-page">
      <section className="tutorials-hero reveal" aria-label="Tutorials hero section">
        <div className="hero-premium-shell">
          <div className="hero-glow hero-glow-left" />
          <div className="hero-glow hero-glow-right" />

          <div className="hero-content">
            <h1 className="tut-hero-title">
              <span className="hero-line hero-line-main">Master Data Skills</span>
              <span className="hero-line hero-line-main">That Build Real Careers</span>
              <span className="hero-line hero-line-accent">
                Learn <span className="tgrad">Excel, SQL, Python, Power BI & More</span>
              </span>
            </h1>

            <p className="tut-hero-sub">
              NextSkillAI gives you structured, beginner-friendly tutorials in Excel,
              SQL, Python, Power BI, and analytics — built to teach practical skills,
              not fill time.
            </p>
          </div>

          <div className="hero-stats-card">
            <div className="tut-stats-row" aria-label="Platform stats">
              <div className="tut-stat">
                <span className="tut-stat-value">{totalTutorials}</span>
                <span className="tut-stat-label">Total Tutorials</span>
              </div>

              <div className="tut-stat-divider" />

              <div className="tut-stat">
                <span className="tut-stat-value">{totalLive}</span>
                <span className="tut-stat-label">Available Now</span>
              </div>

              <div className="tut-stat-divider" />

              <div className="tut-stat">
                <span className="tut-stat-value">{totalSoon}</span>
                <span className="tut-stat-label">Coming Soon</span>
              </div>

              <div className="tut-stat-divider" />

              <div className="tut-stat">
                <span className="tut-stat-value">Free</span>
                <span className="tut-stat-label">To Start</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="filters-wrap reveal" aria-label="Tutorial filters">
        <div className="filters premium-filter-bar" role="group" aria-label="Tutorial filter buttons">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`filter-btn ${activeFilter === filter ? "active-filter" : ""}`}
              onClick={() => setActiveFilter(filter)}
              aria-pressed={activeFilter === filter}
            >
              {filter}
            </button>
          ))}
        </div>
      </section>

      <section className="tutorials-grid-section" aria-label="Tutorial cards">
        <div className="tut-grid">
          {filteredTutorials.length > 0 ? (
            filteredTutorials.map((tutorial, index) => (
              <TutorialCard
                key={tutorial.id}
                tutorial={tutorial}
                index={index}
              />
            ))
          ) : (
            <div className="grid-empty">No tutorials found for this filter.</div>
          )}
        </div>
      </section>

      <section className="tut-cta reveal" aria-label="Future content section">
        <div className="tut-cta-card">
          <h2 className="tut-cta-heading">
            Data Skills · Automation · ML · <span className="tgrad">Real Projects</span>
          </h2>

          <p className="tut-cta-sub">
            More advanced tutorials are coming with practical workflows, hands-on
            projects, and modern tools. NextSkillAI is built for learners who want
            real skills, real confidence, and real career growth.
          </p>
        </div>
      </section>
    </div>
  );
}

export default Tutorials;